import { Container, Graphics, Sprite, Text } from 'pixi.js';
import {
  createBackgroundGalleryImageReference,
  createCharacterExpressionImageReference,
  createEvidenceGalleryImageReference,
  createSecretGalleryImageReference,
  findGalleryCase,
  listBackgroundGalleryEntries,
  listCharacterGalleryEntries,
  listEvidenceGalleryEntries,
  listGalleryCases,
  listSecretGalleryEntries,
  type BackgroundGalleryEntry,
  type CharacterGalleryEntry,
  type EvidenceGalleryEntry,
  type GalleryTabId,
  type SecretGalleryEntry
} from '../app/GalleryCatalog';
import {
  createGalleryUnlockState,
  isGalleryItemUnlocked,
  type GalleryUnlockState
} from '../app/GalleryUnlock';
import type { CharacterExpressionDefinition, GameStateSnapshot } from '../app/types';
import type { ViewportLayout } from '../app/Viewport';
import type { AssetReference } from '../systems/AssetLoader';
import { Button, Panel, UI_COLORS, UI_FONT_FAMILY, fitTextToBounds } from '../ui';
import { BaseScene, type SceneServices } from './BaseScene';

export interface GallerySceneOptions {
  onBack?: () => void;
  onGoToTitle?: () => void;
}

interface TabButtonEntry {
  id: GalleryTabId;
  button: Button;
}

const PANEL_WIDTH = 1148;
const PANEL_HEIGHT = 532;
const TAB_WIDTH = 156;
const TAB_HEIGHT = 44;
const TAB_GAP = 12;
const CHARACTER_PREVIEW_X = 18;
const CHARACTER_PREVIEW_Y = 6;
const CHARACTER_PREVIEW_WIDTH = 446;
const CHARACTER_PREVIEW_HEIGHT = 396;
const CHARACTER_INFO_X = 504;
const CHARACTER_INFO_WIDTH = 548;
const MEDIA_PREVIEW_X = 18;
const MEDIA_PREVIEW_Y = 10;
const MEDIA_PREVIEW_WIDTH = 696;
const MEDIA_PREVIEW_HEIGHT = 360;
const MEDIA_INFO_X = 752;
const MEDIA_INFO_WIDTH = 330;
const EVIDENCE_PREVIEW_WIDTH = 488;
const EVIDENCE_INFO_X = 530;
const CONTENT_WIDTH = PANEL_WIDTH - 56;
type GalleryFontWeight = '400' | '500' | '600' | '700' | '800' | '900';

export class GalleryScene extends BaseScene {
  private readonly background = new Graphics();
  private readonly titleText = new Text({
    text: 'ギャラリー',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 34,
      fontWeight: '900'
    }
  });
  private readonly subtitleText = new Text({
    text: '解放された事件資料',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      fontWeight: '700'
    }
  });
  private readonly tabLayer = new Container();
  private readonly panel = new Panel({
    width: PANEL_WIDTH,
    height: PANEL_HEIGHT,
    title: '資料閲覧'
  });
  private readonly contentLayer = new Container();
  private readonly backButton = new Button({
    label: '戻る',
    width: 150,
    height: 48,
    variant: 'ghost',
    onPress: () => this.options.onBack?.()
  });
  private readonly titleButton = new Button({
    label: 'タイトルへ',
    width: 180,
    height: 48,
    variant: 'secondary',
    onPress: () => this.options.onGoToTitle?.()
  });

  private readonly tabButtons: TabButtonEntry[] = [];
  private unlockState: GalleryUnlockState = createGalleryUnlockState([], listGalleryCases());
  private characterEntries: CharacterGalleryEntry[] = [];
  private backgroundEntries: BackgroundGalleryEntry[] = [];
  private evidenceEntries: EvidenceGalleryEntry[] = [];
  private secretEntries: SecretGalleryEntry[] = [];
  private currentTab: GalleryTabId = 'characters';
  private selectedCharacterIndex = 0;
  private selectedExpressionIndex = 0;
  private selectedBackgroundIndex = 0;
  private selectedEvidenceIndex = 0;
  private selectedSecretIndex = 0;
  private imageLoadToken = 0;

  public constructor(
    services: SceneServices,
    private readonly options: GallerySceneOptions = {}
  ) {
    super(services);
  }

  protected override onStart(): void {
    this.container.addChild(this.background);
    this.container.addChild(this.titleText);
    this.container.addChild(this.subtitleText);
    this.container.addChild(this.tabLayer);
    this.container.addChild(this.panel);
    this.container.addChild(this.backButton);
    this.container.addChild(this.titleButton);
    this.panel.content.addChild(this.contentLayer);
    this.refreshUnlockedEntries();
    this.renderTabs();
    this.renderCurrentTab();
    this.resize(this.services.getLayout());
  }

  public override resize(layout: ViewportLayout): void {
    this.drawBackground(layout.baseWidth, layout.baseHeight);
    this.titleText.position.set(layout.safeArea.x, layout.safeArea.y + 16);
    this.subtitleText.position.set(layout.safeArea.x + 4, layout.safeArea.y + 60);
    this.tabLayer.position.set(layout.safeArea.x + 344, layout.safeArea.y + 28);
    this.panel.position.set(Math.round((layout.baseWidth - PANEL_WIDTH) / 2), 128);
    this.backButton.position.set(
      layout.safeArea.x,
      layout.safeArea.y + layout.safeArea.height - 58
    );
    this.titleButton.position.set(
      layout.safeArea.x + layout.safeArea.width - 180,
      layout.safeArea.y + layout.safeArea.height - 58
    );
  }

  private refreshUnlockedEntries(): void {
    const snapshots = this.getSaveSnapshots();
    this.unlockState = createGalleryUnlockState(snapshots, listGalleryCases());
    this.characterEntries = listCharacterGalleryEntries();
    this.backgroundEntries = listBackgroundGalleryEntries();
    this.evidenceEntries = listEvidenceGalleryEntries();
    this.secretEntries = this.unlockState.allUnlocked ? listSecretGalleryEntries() : [];

    this.selectedCharacterIndex = clampIndex(this.selectedCharacterIndex, this.characterEntries);
    this.selectedBackgroundIndex = clampIndex(this.selectedBackgroundIndex, this.backgroundEntries);
    this.selectedEvidenceIndex = clampIndex(this.selectedEvidenceIndex, this.evidenceEntries);
    this.selectedSecretIndex = clampIndex(this.selectedSecretIndex, this.secretEntries);
    if (this.currentTab === 'secrets' && this.secretEntries.length === 0) {
      this.currentTab = 'characters';
    }
  }

  private getSaveSnapshots(): GameStateSnapshot[] {
    const snapshots: GameStateSnapshot[] = [];
    for (const slot of this.services.getSaveManager().listSlots()) {
      if (slot.status !== 'valid') {
        continue;
      }

      const result = this.services.getSaveManager().loadSlot(slot.slotId);
      if (result.status === 'valid' && result.record) {
        snapshots.push(result.record.snapshot);
      }
    }
    return snapshots;
  }

  private renderTabs(): void {
    for (const entry of this.tabButtons.splice(0)) {
      entry.button.destroy({ children: true });
    }
    this.tabLayer.removeChildren();

    const tabs: Array<{ id: GalleryTabId; label: string }> = [
      { id: 'characters', label: 'キャラクター' },
      { id: 'backgrounds', label: '背景' },
      { id: 'evidences', label: '証拠品' }
    ];
    if (this.secretEntries.length > 0) {
      tabs.push({ id: 'secrets', label: 'シークレット' });
    }

    for (const [index, tab] of tabs.entries()) {
      const button = new Button({
        label: tab.label,
        width: TAB_WIDTH,
        height: TAB_HEIGHT,
        variant: tab.id === this.currentTab ? 'primary' : 'secondary',
        onPress: () => this.selectTab(tab.id)
      });
      button.position.set(index * (TAB_WIDTH + TAB_GAP), 0);
      this.tabButtons.push({ id: tab.id, button });
      this.tabLayer.addChild(button);
    }
  }

  private selectTab(tabId: GalleryTabId): void {
    if (this.currentTab === tabId) {
      return;
    }

    this.currentTab = tabId;
    for (const entry of this.tabButtons) {
      entry.button.setVariant(entry.id === tabId ? 'primary' : 'secondary');
    }
    this.renderCurrentTab();
  }

  private renderCurrentTab(): void {
    this.clearContent();
    switch (this.currentTab) {
      case 'characters':
        this.renderCharacterGallery();
        break;
      case 'backgrounds':
        this.renderBackgroundGallery();
        break;
      case 'evidences':
        this.renderEvidenceGallery();
        break;
      case 'secrets':
        this.renderSecretGallery();
        break;
    }
  }

  private renderCharacterGallery(): void {
    const entry = this.characterEntries[this.selectedCharacterIndex];
    if (!entry) {
      this.renderEmptyMessage(
        'キャラクター未解放',
        'セーブデータで事件を進めると、登場済みキャラクターがここに追加されます。'
      );
      return;
    }

    if (!this.isEntryUnlocked(entry.caseIds)) {
      this.renderLockedCharacterGallery(entry);
      return;
    }

    const visibleExpressions = this.getVisibleCharacterExpressions(entry);
    this.selectedExpressionIndex = clampIndex(this.selectedExpressionIndex, visibleExpressions);
    const expression = visibleExpressions[this.selectedExpressionIndex];

    this.drawImageFrame(
      CHARACTER_PREVIEW_X,
      CHARACTER_PREVIEW_Y,
      CHARACTER_PREVIEW_WIDTH,
      CHARACTER_PREVIEW_HEIGHT
    );
    this.renderImage(createCharacterExpressionImageReference(entry.character, expression), {
      x: CHARACTER_PREVIEW_X,
      y: CHARACTER_PREVIEW_Y,
      width: CHARACTER_PREVIEW_WIDTH,
      height: CHARACTER_PREVIEW_HEIGHT,
      mode: 'contain',
      placeholder: '立ち絵未配置'
    });

    const expressionPrevButton = new Button({
      label: '←',
      width: 58,
      height: 58,
      variant: 'secondary',
      onPress: () => this.shiftExpression(-1)
    });
    const expressionNextButton = new Button({
      label: '→',
      width: 58,
      height: 58,
      variant: 'secondary',
      onPress: () => this.shiftExpression(1)
    });
    expressionPrevButton.position.set(CHARACTER_PREVIEW_X + 14, CHARACTER_PREVIEW_Y + 166);
    expressionNextButton.position.set(
      CHARACTER_PREVIEW_X + CHARACTER_PREVIEW_WIDTH - 72,
      CHARACTER_PREVIEW_Y + 166
    );
    this.contentLayer.addChild(expressionPrevButton, expressionNextButton);

    const previousCharacterButton = new Button({
      label: '前の人物',
      width: 132,
      height: 42,
      variant: 'ghost',
      onPress: () => this.shiftCharacter(-1)
    });
    const nextCharacterButton = new Button({
      label: '次の人物',
      width: 132,
      height: 42,
      variant: 'ghost',
      onPress: () => this.shiftCharacter(1)
    });
    previousCharacterButton.position.set(CHARACTER_PREVIEW_X + 62, 404);
    nextCharacterButton.position.set(CHARACTER_PREVIEW_X + 252, 404);
    this.contentLayer.addChild(previousCharacterButton, nextCharacterButton);

    const nameText = this.createText(entry.character.fullName ?? entry.character.displayName, {
      x: CHARACTER_INFO_X,
      y: 2,
      width: CHARACTER_INFO_WIDTH,
      height: 48,
      fontSize: 32,
      fill: UI_COLORS.gold,
      weight: '900'
    });
    const metaText = this.createText(
      [
        entry.character.displayName,
        getRoleLabel(entry.character.role),
        entry.character.age ? `${entry.character.age}歳` : null,
        `表情: ${expression.label}${expression.unlock === 'finalCaseComplete' ? ' / Case 10クリア特典' : ''}`
      ]
        .filter(Boolean)
        .join(' / '),
      {
        x: CHARACTER_INFO_X,
        y: 56,
        width: CHARACTER_INFO_WIDTH,
        height: 34,
        fontSize: 17,
        fill: UI_COLORS.mutedInk,
        weight: '800'
      }
    );
    const profileText = this.createText(entry.character.profile ?? '紹介文は未登録です。', {
      x: CHARACTER_INFO_X,
      y: 112,
      width: CHARACTER_INFO_WIDTH,
      height: 210,
      fontSize: 22,
      lineHeight: 34,
      fill: UI_COLORS.ink,
      weight: '700'
    });
    const caseText = this.createText(`登場: ${formatCaseUsage(entry.caseIds)}`, {
      x: CHARACTER_INFO_X,
      y: 344,
      width: CHARACTER_INFO_WIDTH,
      height: 66,
      fontSize: 16,
      lineHeight: 24,
      fill: UI_COLORS.mutedInk,
      weight: '700'
    });
    this.contentLayer.addChild(nameText, metaText, profileText, caseText);
  }

  private renderLockedCharacterGallery(entry: CharacterGalleryEntry): void {
    this.renderLockedThumbnail(
      CHARACTER_PREVIEW_X,
      CHARACTER_PREVIEW_Y,
      CHARACTER_PREVIEW_WIDTH,
      CHARACTER_PREVIEW_HEIGHT,
      '対象事件へ到達すると表示'
    );

    const previousCharacterButton = new Button({
      label: '前の人物',
      width: 132,
      height: 42,
      variant: 'ghost',
      onPress: () => this.shiftCharacter(-1)
    });
    const nextCharacterButton = new Button({
      label: '次の人物',
      width: 132,
      height: 42,
      variant: 'ghost',
      onPress: () => this.shiftCharacter(1)
    });
    previousCharacterButton.position.set(CHARACTER_PREVIEW_X + 62, 404);
    nextCharacterButton.position.set(CHARACTER_PREVIEW_X + 252, 404);
    this.contentLayer.addChild(previousCharacterButton, nextCharacterButton);

    const titleText = this.createText('未解放の人物資料', {
      x: CHARACTER_INFO_X,
      y: 2,
      width: CHARACTER_INFO_WIDTH,
      height: 48,
      fontSize: 32,
      fill: UI_COLORS.gold,
      weight: '900'
    });
    const conditionText = this.createText(formatUnlockRequirement(entry.caseIds), {
      x: CHARACTER_INFO_X,
      y: 56,
      width: CHARACTER_INFO_WIDTH,
      height: 34,
      fontSize: 17,
      fill: UI_COLORS.mutedInk,
      weight: '800'
    });
    const bodyText = this.createText(
      '対象の事件まで進行したセーブデータがあると、この人物の立ち絵とプロフィールを閲覧できます。',
      {
        x: CHARACTER_INFO_X,
        y: 112,
        width: CHARACTER_INFO_WIDTH,
        height: 166,
        fontSize: 22,
        lineHeight: 34,
        fill: UI_COLORS.ink,
        weight: '700'
      }
    );
    const statusText = this.createText(getUnlockStatusText(this.unlockState), {
      x: CHARACTER_INFO_X,
      y: 322,
      width: CHARACTER_INFO_WIDTH,
      height: 66,
      fontSize: 16,
      lineHeight: 24,
      fill: UI_COLORS.mutedInk,
      weight: '700'
    });
    this.contentLayer.addChild(titleText, conditionText, bodyText, statusText);
  }

  private renderBackgroundGallery(): void {
    const entry = this.backgroundEntries[this.selectedBackgroundIndex];
    if (!entry) {
      this.renderEmptyMessage(
        '背景未解放',
        '対象の事件まで進行したセーブデータがあると、使用済み背景が閲覧できます。'
      );
      return;
    }

    if (!this.isEntryUnlocked(entry.caseIds)) {
      this.renderLockedMediaGallery({
        title: '未解放の背景資料',
        subtitle: formatUnlockRequirement(entry.caseIds),
        body: '対象の事件まで進行したセーブデータがあると、この背景画像を閲覧できます。',
        caseIds: entry.caseIds,
        onPrevious: () => this.shiftBackground(-1),
        onNext: () => this.shiftBackground(1),
        index: this.selectedBackgroundIndex,
        total: this.backgroundEntries.length
      });
      return;
    }

    this.renderMediaGallery({
      title: entry.background.label,
      subtitle: getBackgroundCategoryLabel(entry.background.category),
      body: `背景ID: ${entry.background.id}`,
      caseIds: entry.caseIds,
      reference: createBackgroundGalleryImageReference(entry.background),
      imageMode: 'cover',
      placeholder: '背景未配置',
      onPrevious: () => this.shiftBackground(-1),
      onNext: () => this.shiftBackground(1),
      index: this.selectedBackgroundIndex,
      total: this.backgroundEntries.length
    });
  }

  private renderEvidenceGallery(): void {
    const entry = this.evidenceEntries[this.selectedEvidenceIndex];
    if (!entry) {
      this.renderEmptyMessage(
        '証拠品未解放',
        '対象の事件まで進行したセーブデータがあると、証拠品画像が閲覧できます。'
      );
      return;
    }

    if (!this.isEntryUnlocked(entry.caseIds)) {
      this.renderLockedMediaGallery({
        title: '未解放の証拠品資料',
        subtitle: formatUnlockRequirement(entry.caseIds),
        body: '対象の事件まで進行したセーブデータがあると、この証拠品画像と詳細を閲覧できます。',
        caseIds: entry.caseIds,
        onPrevious: () => this.shiftEvidence(-1),
        onNext: () => this.shiftEvidence(1),
        index: this.selectedEvidenceIndex,
        total: this.evidenceEntries.length,
        imageWidth: EVIDENCE_PREVIEW_WIDTH,
        infoX: EVIDENCE_INFO_X,
        infoWidth: CONTENT_WIDTH - EVIDENCE_INFO_X
      });
      return;
    }

    this.renderMediaGallery({
      title: entry.evidence.name,
      subtitle: entry.evidence.shortName ?? '事件ファイルの手がかり',
      body: `${entry.evidence.description}\n\n${entry.evidence.detail ?? ''}`.trim(),
      caseIds: entry.caseIds,
      reference: createEvidenceGalleryImageReference(entry.evidence),
      imageMode: 'contain',
      placeholder: '証拠画像未配置',
      onPrevious: () => this.shiftEvidence(-1),
      onNext: () => this.shiftEvidence(1),
      index: this.selectedEvidenceIndex,
      total: this.evidenceEntries.length,
      imageWidth: EVIDENCE_PREVIEW_WIDTH,
      infoX: EVIDENCE_INFO_X,
      infoWidth: CONTENT_WIDTH - EVIDENCE_INFO_X
    });
  }

  private renderSecretGallery(): void {
    const entry = this.secretEntries[this.selectedSecretIndex];
    if (!entry) {
      this.renderEmptyMessage(
        'シークレット未登録',
        'Case 10クリア後に解放する追加画像は、後から gallery.json に登録できます。'
      );
      return;
    }

    this.renderMediaGallery({
      title: entry.label,
      subtitle: 'シークレット',
      body: entry.description ?? '追加ギャラリー画像です。',
      caseIds: entry.caseIds,
      reference: createSecretGalleryImageReference(entry),
      imageMode: 'contain',
      placeholder: 'シークレット画像未配置',
      onPrevious: () => this.shiftSecret(-1),
      onNext: () => this.shiftSecret(1),
      index: this.selectedSecretIndex,
      total: this.secretEntries.length
    });
  }

  private renderLockedMediaGallery(options: {
    title: string;
    subtitle: string;
    body: string;
    caseIds: readonly string[];
    onPrevious: () => void;
    onNext: () => void;
    index: number;
    total: number;
    imageWidth?: number;
    infoX?: number;
    infoWidth?: number;
  }): void {
    const imageWidth = options.imageWidth ?? MEDIA_PREVIEW_WIDTH;
    const infoX = options.infoX ?? MEDIA_INFO_X;
    const infoWidth = options.infoWidth ?? MEDIA_INFO_WIDTH;

    this.renderLockedThumbnail(
      MEDIA_PREVIEW_X,
      MEDIA_PREVIEW_Y,
      imageWidth,
      MEDIA_PREVIEW_HEIGHT,
      '対象事件へ到達すると表示'
    );

    const titleText = this.createText(options.title, {
      x: infoX,
      y: 8,
      width: infoWidth,
      height: 76,
      fontSize: 29,
      lineHeight: 36,
      fill: UI_COLORS.gold,
      weight: '900'
    });
    const subtitleText = this.createText(options.subtitle, {
      x: infoX,
      y: 92,
      width: infoWidth,
      height: 34,
      fontSize: 17,
      fill: UI_COLORS.mutedInk,
      weight: '800'
    });
    const bodyText = this.createText(options.body, {
      x: infoX,
      y: 142,
      width: infoWidth,
      height: 166,
      fontSize: 20,
      lineHeight: 30,
      fill: UI_COLORS.ink,
      weight: '700'
    });
    const usageText = this.createText(formatUnlockRequirement(options.caseIds), {
      x: 18,
      y: 382,
      width: CONTENT_WIDTH - 36,
      height: 48,
      fontSize: 16,
      lineHeight: 23,
      fill: UI_COLORS.mutedInk,
      weight: '700'
    });
    const counterText = this.createText(`${options.index + 1} / ${options.total}`, {
      x: infoX,
      y: 330,
      width: infoWidth,
      height: 26,
      fontSize: 16,
      fill: UI_COLORS.mutedInk,
      weight: '800'
    });
    this.contentLayer.addChild(titleText, subtitleText, bodyText, usageText, counterText);
    this.addPagingButtons(options.onPrevious, options.onNext, infoX, 372);
  }

  private renderMediaGallery(options: {
    title: string;
    subtitle: string;
    body: string;
    caseIds: readonly string[];
    reference: AssetReference;
    imageMode: 'contain' | 'cover';
    placeholder: string;
    onPrevious: () => void;
    onNext: () => void;
    index: number;
    total: number;
    imageWidth?: number;
    infoX?: number;
    infoWidth?: number;
  }): void {
    const imageWidth = options.imageWidth ?? MEDIA_PREVIEW_WIDTH;
    const infoX = options.infoX ?? MEDIA_INFO_X;
    const infoWidth = options.infoWidth ?? MEDIA_INFO_WIDTH;

    this.drawImageFrame(MEDIA_PREVIEW_X, MEDIA_PREVIEW_Y, imageWidth, MEDIA_PREVIEW_HEIGHT);
    this.renderImage(options.reference, {
      x: MEDIA_PREVIEW_X,
      y: MEDIA_PREVIEW_Y,
      width: imageWidth,
      height: MEDIA_PREVIEW_HEIGHT,
      mode: options.imageMode,
      placeholder: options.placeholder
    });

    const titleText = this.createText(options.title, {
      x: infoX,
      y: 8,
      width: infoWidth,
      height: 76,
      fontSize: 29,
      lineHeight: 36,
      fill: UI_COLORS.gold,
      weight: '900'
    });
    const subtitleText = this.createText(options.subtitle, {
      x: infoX,
      y: 92,
      width: infoWidth,
      height: 34,
      fontSize: 17,
      fill: UI_COLORS.mutedInk,
      weight: '800'
    });
    const bodyText = this.createText(options.body, {
      x: infoX,
      y: 142,
      width: infoWidth,
      height: 166,
      fontSize: 20,
      lineHeight: 30,
      fill: UI_COLORS.ink,
      weight: '700'
    });
    const usageText = this.createText(`使用: ${formatCaseUsage(options.caseIds)}`, {
      x: 18,
      y: 382,
      width: CONTENT_WIDTH - 36,
      height: 48,
      fontSize: 16,
      lineHeight: 23,
      fill: UI_COLORS.mutedInk,
      weight: '700'
    });
    const counterText = this.createText(`${options.index + 1} / ${options.total}`, {
      x: infoX,
      y: 330,
      width: infoWidth,
      height: 26,
      fontSize: 16,
      fill: UI_COLORS.mutedInk,
      weight: '800'
    });
    this.contentLayer.addChild(titleText, subtitleText, bodyText, usageText, counterText);
    this.addPagingButtons(options.onPrevious, options.onNext, infoX, 372);
  }

  private addPagingButtons(onPrevious: () => void, onNext: () => void, x: number, y: number): void {
    const previousButton = new Button({
      label: '前へ',
      width: 120,
      height: 42,
      variant: 'ghost',
      onPress: onPrevious
    });
    const nextButton = new Button({
      label: '次へ',
      width: 120,
      height: 42,
      variant: 'secondary',
      onPress: onNext
    });
    previousButton.position.set(x, y);
    nextButton.position.set(x + 146, y);
    this.contentLayer.addChild(previousButton, nextButton);
  }

  private shiftCharacter(delta: number): void {
    if (this.characterEntries.length === 0) {
      return;
    }

    this.selectedCharacterIndex = wrapIndex(
      this.selectedCharacterIndex + delta,
      this.characterEntries.length
    );
    this.selectedExpressionIndex = 0;
    this.renderCurrentTab();
  }

  private shiftExpression(delta: number): void {
    const entry = this.characterEntries[this.selectedCharacterIndex];
    if (!entry) {
      return;
    }

    const visibleExpressions = this.getVisibleCharacterExpressions(entry);
    if (visibleExpressions.length === 0) {
      return;
    }

    this.selectedExpressionIndex = wrapIndex(
      this.selectedExpressionIndex + delta,
      visibleExpressions.length
    );
    this.renderCurrentTab();
  }

  private getVisibleCharacterExpressions(
    entry: CharacterGalleryEntry
  ): CharacterExpressionDefinition[] {
    if (this.unlockState.finalCaseCompleted) {
      return entry.character.expressions;
    }

    return entry.character.expressions.filter(
      (expression) => expression.unlock !== 'finalCaseComplete'
    );
  }

  private shiftBackground(delta: number): void {
    this.selectedBackgroundIndex = wrapIndex(
      this.selectedBackgroundIndex + delta,
      this.backgroundEntries.length
    );
    this.renderCurrentTab();
  }

  private shiftEvidence(delta: number): void {
    this.selectedEvidenceIndex = wrapIndex(
      this.selectedEvidenceIndex + delta,
      this.evidenceEntries.length
    );
    this.renderCurrentTab();
  }

  private shiftSecret(delta: number): void {
    this.selectedSecretIndex = wrapIndex(
      this.selectedSecretIndex + delta,
      this.secretEntries.length
    );
    this.renderCurrentTab();
  }

  private renderEmptyMessage(title: string, message: string): void {
    const titleText = this.createText(title, {
      x: 32,
      y: 72,
      width: CONTENT_WIDTH - 64,
      height: 54,
      fontSize: 32,
      fill: UI_COLORS.gold,
      weight: '900'
    });
    const messageText = this.createText(message, {
      x: 32,
      y: 144,
      width: CONTENT_WIDTH - 64,
      height: 120,
      fontSize: 22,
      lineHeight: 34,
      fill: UI_COLORS.ink,
      weight: '700'
    });
    const statusText = this.createText(getUnlockStatusText(this.unlockState), {
      x: 32,
      y: 310,
      width: CONTENT_WIDTH - 64,
      height: 70,
      fontSize: 17,
      lineHeight: 25,
      fill: UI_COLORS.mutedInk,
      weight: '700'
    });
    this.contentLayer.addChild(titleText, messageText, statusText);
  }

  private renderImage(
    reference: AssetReference,
    options: {
      x: number;
      y: number;
      width: number;
      height: number;
      mode: 'contain' | 'cover';
      placeholder: string;
    }
  ): void {
    const token = ++this.imageLoadToken;
    const placeholderText = this.createText('画像読込中', {
      x: options.x + 18,
      y: options.y + options.height / 2 - 18,
      width: options.width - 36,
      height: 42,
      fontSize: 18,
      fill: UI_COLORS.mutedInk,
      weight: '900',
      align: 'center'
    });
    this.contentLayer.addChild(placeholderText);

    void this.services
      .getAssets()
      .loadImage(reference)
      .then((image) => {
        if (token !== this.imageLoadToken) {
          return;
        }

        if (image.placeholder) {
          placeholderText.text = options.placeholder;
          return;
        }

        placeholderText.visible = false;
        const sprite = new Sprite(image.texture);
        if (options.mode === 'cover') {
          coverSprite(sprite, options.width - 16, options.height - 16);
        } else {
          containSprite(sprite, options.width - 16, options.height - 16);
        }
        sprite.position.set(
          options.x + 8 + (options.width - 16 - sprite.width) / 2,
          options.y + 8 + (options.height - 16 - sprite.height) / 2
        );

        const mask = new Graphics();
        mask
          .roundRect(options.x + 8, options.y + 8, options.width - 16, options.height - 16, 5)
          .fill(UI_COLORS.ink);
        sprite.mask = mask;
        this.contentLayer.addChildAt(sprite, this.contentLayer.getChildIndex(placeholderText));
        this.contentLayer.addChild(mask);
        this.drawImageFrame(options.x, options.y, options.width, options.height, true);
      });
  }

  private renderLockedThumbnail(
    x: number,
    y: number,
    width: number,
    height: number,
    message: string
  ): void {
    this.drawImageFrame(x, y, width, height);
    const overlay = new Graphics();
    overlay
      .roundRect(x + 8, y + 8, width - 16, height - 16, 5)
      .fill({ color: UI_COLORS.black, alpha: 0.58 })
      .stroke({ color: UI_COLORS.mutedInk, width: 1, alpha: 0.5 });

    const lockText = this.createText('LOCKED', {
      x: x + 32,
      y: y + height / 2 - 44,
      width: width - 64,
      height: 48,
      fontSize: 32,
      fill: UI_COLORS.gold,
      weight: '900',
      align: 'center'
    });
    const messageText = this.createText(message, {
      x: x + 32,
      y: y + height / 2 + 8,
      width: width - 64,
      height: 44,
      fontSize: 18,
      fill: UI_COLORS.mutedInk,
      weight: '800',
      align: 'center'
    });

    this.contentLayer.addChild(overlay, lockText, messageText);
  }

  private drawImageFrame(
    x: number,
    y: number,
    width: number,
    height: number,
    overlay = false
  ): void {
    const frame = new Graphics();
    frame
      .roundRect(x, y, width, height, 7)
      .fill({ color: UI_COLORS.black, alpha: overlay ? 0 : 0.44 })
      .stroke({ color: UI_COLORS.gold, width: 2, alpha: 0.72 });
    frame.rect(x + 14, y + height - 18, width - 28, 2).fill({
      color: UI_COLORS.gold,
      alpha: 0.32
    });
    this.contentLayer.addChild(frame);
  }

  private createText(
    text: string,
    options: {
      x: number;
      y: number;
      width: number;
      height: number;
      fontSize: number;
      fill: number;
      weight: GalleryFontWeight;
      lineHeight?: number;
      align?: 'left' | 'center';
    }
  ): Text {
    const label = new Text({
      text,
      style: {
        fill: options.fill,
        fontFamily: UI_FONT_FAMILY,
        fontSize: options.fontSize,
        fontWeight: options.weight,
        lineHeight: options.lineHeight ?? Math.round(options.fontSize * 1.24),
        align: options.align ?? 'left',
        wordWrap: true,
        wordWrapWidth: options.width
      }
    });
    label.position.set(options.x, options.y);
    fitTextToBounds(label, {
      maxWidth: options.width,
      maxHeight: options.height,
      baseFontSize: options.fontSize,
      minFontSize: Math.max(12, Math.round(options.fontSize * 0.62)),
      baseLineHeight: options.lineHeight ?? Math.round(options.fontSize * 1.24),
      minLineHeight: Math.max(16, Math.round((options.lineHeight ?? options.fontSize) * 0.72)),
      wordWrap: true,
      breakWords: true
    });
    return label;
  }

  private clearContent(): void {
    this.imageLoadToken += 1;
    for (const child of this.contentLayer.removeChildren()) {
      child.destroy({ children: true });
    }
  }

  private drawBackground(width: number, height: number): void {
    this.background.clear();
    this.background.rect(0, 0, width, height).fill(UI_COLORS.black);
    this.background.rect(0, 0, width, height).fill({ color: UI_COLORS.redDark, alpha: 0.2 });
    this.background.rect(0, 0, width, 8).fill({ color: UI_COLORS.red, alpha: 0.84 });
    this.background.rect(0, height - 8, width, 8).fill({ color: UI_COLORS.gold, alpha: 0.72 });
  }

  private isEntryUnlocked(caseIds: readonly string[]): boolean {
    return isGalleryItemUnlocked(caseIds, this.unlockState);
  }
}

function formatCaseUsage(caseIds: readonly string[]): string {
  if (caseIds.length === 0) {
    return 'Case 10クリア後';
  }

  return caseIds
    .map((caseId) => findGalleryCase(caseId))
    .filter((caseDefinition): caseDefinition is NonNullable<typeof caseDefinition> =>
      Boolean(caseDefinition)
    )
    .sort((left, right) => left.displayOrder - right.displayOrder)
    .map((caseDefinition) => `Case ${caseDefinition.displayOrder}「${caseDefinition.title}」`)
    .join(' / ');
}

function formatUnlockRequirement(caseIds: readonly string[]): string {
  const firstCase = caseIds
    .map((caseId) => findGalleryCase(caseId))
    .filter((caseDefinition): caseDefinition is NonNullable<typeof caseDefinition> =>
      Boolean(caseDefinition)
    )
    .sort((left, right) => left.displayOrder - right.displayOrder)[0];

  if (!firstCase) {
    return '解放条件: Case 10クリア後';
  }

  return `解放条件: Case ${firstCase.displayOrder}「${firstCase.title}」到達`;
}

function getUnlockStatusText(unlockState: GalleryUnlockState): string {
  if (!unlockState.hasSaveData) {
    return '解放状況: 初期公開資料のみ';
  }

  if (unlockState.maxReachedOrder < 0) {
    return '解放状況: セーブデータなし';
  }

  return unlockState.allUnlocked
    ? '解放状況: Case 8完了 / 全資料解放'
    : `解放状況: Case ${unlockState.maxReachedOrder} まで到達`;
}

function getRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    protagonist: '主人公',
    detective: '探偵',
    heroine: 'ヒロイン',
    support: '協力者',
    police: '警察',
    rival: 'ライバル探偵',
    phantom_thief: '怪盗',
    suspect: '関係者',
    victim: '被害者',
    resident: '住民',
    narrator: '語り'
  };
  return labels[role] ?? role;
}

function getBackgroundCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    town: '街エリア',
    detective_police: '探偵・警察関連',
    crime_scene: '事件現場',
    daily: '日常',
    ominous: '不穏・異常',
    transition: '演出背景',
    ui: 'UI背景'
  };
  return labels[category] ?? category;
}

function wrapIndex(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }

  return ((index % length) + length) % length;
}

function clampIndex(index: number, entries: readonly unknown[]): number {
  if (entries.length === 0) {
    return 0;
  }

  return Math.max(0, Math.min(index, entries.length - 1));
}

function containSprite(sprite: Sprite, width: number, height: number): void {
  const scale = Math.min(width / getTextureWidth(sprite), height / getTextureHeight(sprite));
  sprite.scale.set(scale);
}

function coverSprite(sprite: Sprite, width: number, height: number): void {
  const scale = Math.max(width / getTextureWidth(sprite), height / getTextureHeight(sprite));
  sprite.scale.set(scale);
}

function getTextureWidth(sprite: Sprite): number {
  return Math.max(1, sprite.texture.width);
}

function getTextureHeight(sprite: Sprite): number {
  return Math.max(1, sprite.texture.height);
}
